import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import {
  BorrowerType,
  FinancialStatementForm,
  type TQuarter,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';

export const INITIAL_BANK_OPTIONS_COUNT = 20;
export const MAX_DECIMAL_FRACTION_DIGITS = 5;
export const PRIORITY_CURRENCY_CODES = ['UAH', 'USD', 'EUR'] as const;
export const FINANCIAL_STATEMENT_TEMPLATE_EXPENSE_COMPONENTS = [
  'Фінансові витрати',
  'Амортизація',
] as const;
export const REQUIRED_LIABILITY_REPORT_LINE_CODES = ['1690'] as const;
export const REQUIRED_RECEIVABLE_TEMPLATE_CODES = ['1155'] as const;
export const REQUIRED_EXPENSE_REPORT_LINE_CODES = ['2250', '2515'] as const;

export const FINANCIAL_CONDITION_VALIDATION_MESSAGES = {
  reportDate: 'Оберіть дату',
  financialStatementForm: 'Оберіть тип фінансової звітності',
  requiredTemplateRow: 'Заповніть хоча б одне поле обов’язкового рядка',
} as const;

export const FINANCIAL_CONDITION_TEMPLATE_FOOTNOTES = {
  bankIncome:
    'Зазначається повний перелік інших банків/небанківських фінансових установ, в яких відкриті поточні рахунки, навіть у разі відсутності надходжень. У разі наявності надходжень у валюті, яка не передбачена формою, слід додати рядки та зазначити необхідну інформацію.',
  creditOperations:
    'Зазначається відповідна інформація або дата та номер довідки з банку, в якій наведена відповідна інформація. Зазначається інформація про всі договори, що передбачають отримання Позичальником кредитів у будь-яких формах, укладених з банківськими установами, у тому числі нерезидентами України, у тому числі при відсутності заборгованості.',
  financialAssistance: 'У разі відсутності фінансових допомог слід залишити відповідний коментар.',
  offBalanceLiabilities:
    'Зазначається інформація про всі договори, що передбачають позабалансові зобов’язання Позичальника, укладені з іншими банківськими установами, у тому числі нерезидентами України. Тип зобов’язання: гарантії, акредитиви, лізингові операції, авалі, поручительства, передача в заставу майна за зобов’язаннями третіх осіб.',
  currencyExchanges:
    'Зазначається повний перелік банків, де відкриті поточні рахунки, навіть у разі відсутності операцій купівлі-продажу валюти. У разі наявності операцій з купівлі-продажу валюти, яка не передбачена формою, слід додати рядки та зазначити необхідну інформацію.',
} as const;

export const FINANCIAL_CONDITION_FIELD_LABELS = {
  bank: 'Банк',
  currency: 'Валюта',
  maturityDate: 'Дата погашення',
  balanceLine: 'Рядок балансу',
  reportLine: 'Рядок фінансової звітності',
  amountThousands: 'Сума, тис. од.',
  sharePercent: 'Питома вага, %',
} as const;

export const BORROWER_TYPE_OPTIONS = [
  { value: BorrowerType.LegalEntity, label: 'Юридична особа' },
  { value: BorrowerType.SoleTrader, label: 'Фізична особа-підприємець (ФОП)' },
];

export const FINANCIAL_STATEMENT_FORM_OPTIONS: TAutocompleteOption[] = [
  { value: FinancialStatementForm.Large, label: 'Велика форма звітності' },
  {
    value: FinancialStatementForm.SmallOrSimplified,
    label: 'Мала форма звітності / Спрощена форма звітності',
  },
];

type TExpenseTemplateRow = {
  reportLine?: string;
  component?: string;
};

type TFinancialStatementFormTemplate = {
  liabilitiesAnnual: readonly string[];
  liabilitiesQuarterly: readonly string[];
  financialInvestmentCodes: readonly string[];
  receivableCodes: readonly string[];
  expensesAnnual: readonly TExpenseTemplateRow[];
  expensesQuarterly: readonly TExpenseTemplateRow[];
  expensesQuarterlySmall: readonly TExpenseTemplateRow[];
};

export const FINANCIAL_STATEMENT_FORM_TEMPLATES: Record<
  FinancialStatementForm,
  TFinancialStatementFormTemplate
> = {
  [FinancialStatementForm.Large]: {
    liabilitiesAnnual: ['1510', '1515', '1525', '1600', '1610', '1690'],
    liabilitiesQuarterly: ['1510'],
    financialInvestmentCodes: ['1030', '1035', '1160'],
    receivableCodes: ['1120', '1125', '1155', '1190', '1605'],
    expensesAnnual: [
      { reportLine: '2180' },
      { reportLine: '2270' },
      { reportLine: '2150' },
      { reportLine: '2200' },
      { reportLine: '2220' },
      { reportLine: '2240' },
      { reportLine: '2255' },
    ],
    expensesQuarterly: [{ reportLine: '2180' }, { reportLine: '2270' }],
    expensesQuarterlySmall: [],
  },
  [FinancialStatementForm.SmallOrSimplified]: {
    liabilitiesAnnual: ['1595', '1600', '1690'],
    liabilitiesQuarterly: ['1595'],
    financialInvestmentCodes: ['1030', '1160'],
    receivableCodes: ['1090', '1125', '1155', '1190'],
    expensesAnnual: [{ reportLine: '2180' }, { reportLine: '2270' }],
    expensesQuarterly: [],
    expensesQuarterlySmall: [
      { reportLine: '2250', component: 'Фінансові витрати' },
      { reportLine: '2515', component: 'Амортизація' },
    ],
  },
};

export const QUARTER_OPTIONS: TAutocompleteOption[] = [
  { value: 'quarter:1', label: 'I квартал' },
  { value: 'quarter:2', label: 'II квартал' },
  { value: 'quarter:3', label: 'III квартал' },
  { value: 'quarter:4', label: 'IV квартал' },
];

export const MONTH_OPTIONS: TAutocompleteOption[] = [
  { value: 'month:1', label: 'Січень' },
  { value: 'month:2', label: 'Лютий' },
  { value: 'month:3', label: 'Березень' },
  { value: 'month:4', label: 'Квітень' },
  { value: 'month:5', label: 'Травень' },
  { value: 'month:6', label: 'Червень' },
  { value: 'month:7', label: 'Липень' },
  { value: 'month:8', label: 'Серпень' },
  { value: 'month:9', label: 'Вересень' },
  { value: 'month:10', label: 'Жовтень' },
  { value: 'month:11', label: 'Листопад' },
  { value: 'month:12', label: 'Грудень' },
];

export const REPORT_PERIOD_OPTIONS = [...QUARTER_OPTIONS, ...MONTH_OPTIONS];

export const QUARTER_MONTHS: Record<TQuarter, [string, string, string]> = {
  '1': ['Січень', 'Лютий', 'Березень'],
  '2': ['Квітень', 'Травень', 'Червень'],
  '3': ['Липень', 'Серпень', 'Вересень'],
  '4': ['Жовтень', 'Листопад', 'Грудень'],
};

export const CREDIT_PRODUCT_TYPE_OPTIONS: TAutocompleteOption[] = [
  { value: 'loan', label: 'К — кредит' },
  { value: 'nonRevolvingCreditLine', label: 'НКЛ — невідновлювана кредитна лінія' },
  { value: 'revolvingCreditLine', label: 'ВКЛ — відновлювана кредитна лінія' },
  { value: 'trancheLine', label: 'ТЛ — траншева лінія' },
  { value: 'overdraft', label: 'ОВ — овердрафт' },
  { value: 'factoring', label: 'Ф — факторинг' },
  { value: 'financialLeasing', label: 'ФЛ — фінансовий лізинг' },
];

export const CREDIT_PRODUCT_TYPE_CODES: Record<string, string> = {
  loan: 'К',
  nonRevolvingCreditLine: 'НКЛ',
  revolvingCreditLine: 'ВКЛ',
  trancheLine: 'ТЛ',
  overdraft: 'ОВ',
  factoring: 'Ф',
  financialLeasing: 'ФЛ',
};
